import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import type { Drama } from "@/mocks/dramas";
import { genres } from "@/mocks/dramas";
import WatchlistButton from "@/pages/drama/components/WatchlistButton";

export default function DramaHero({ drama }: { drama: Drama }) {
  const genreSlug = genres.find((g) => g.name === drama.category)?.slug ?? "";

  return (
    <section className="relative overflow-hidden border-b border-background-200/60">
      <div className="absolute inset-0">
        <img src={drama.cover} alt="" aria-hidden="true" className="h-full w-full object-cover object-top opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/90 to-background-50"></div>
      </div>

      <div className="relative mx-auto max-w-page px-4 md:px-6">
        <div className="pt-28 md:pt-36">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "排行榜", to: "/ranking" },
              { label: drama.title },
            ]}
          />
        </div>

        <div className="grid grid-cols-1 gap-8 pb-14 pt-6 lg:grid-cols-[300px_1fr] lg:gap-12">
          <div className="mx-auto w-full max-w-[260px] lg:mx-0 lg:max-w-none">
            <div className="overflow-hidden rounded-lg border border-background-200/70">
              <div className="relative w-full" style={{ aspectRatio: "3 / 4" }}>
                <img
                  src={drama.cover}
                  alt={`${drama.title} 短剧海报封面`}
                  title={`${drama.title} 剧集封面`}
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-md border border-background-200/70 bg-background-100/60 px-3 py-3 text-center">
                <div className="flex items-center justify-center gap-1 text-accent-400">
                  <i className="ri-star-fill text-sm" aria-hidden="true"></i>
                  <span className="font-heading text-lg font-bold">{drama.rating.toFixed(1)}</span>
                </div>
                <p className="mt-1 text-[11px] font-label text-foreground-500">编辑评分</p>
              </div>
              <div className="rounded-md border border-background-200/70 bg-background-100/60 px-3 py-3 text-center">
                <div className="flex items-center justify-center gap-1 text-primary-400">
                  <i className="ri-fire-line text-sm" aria-hidden="true"></i>
                  <span className="font-heading text-lg font-bold">{drama.heat}</span>
                </div>
                <p className="mt-1 text-[11px] font-label text-foreground-500">站内热度</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500/90 px-3 py-1 text-xs font-label text-background-950">
                {drama.category}
              </span>
              <span
                className={`rounded-full px-3 py-1 text-xs font-label ${
                  drama.status === "更新中" ? "bg-accent-500 text-background-950" : "bg-secondary-100 text-accent-400"
                }`}
              >
                {drama.status}
              </span>
              {drama.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-300/60 px-3 py-1 text-xs font-label text-foreground-600"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
              {drama.title}
            </h1>
            <p className="mt-3 text-base leading-relaxed text-foreground-700">{drama.subtitle}</p>

            <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm">
              <div className="flex items-center gap-2">
                <dt className="text-foreground-500">集数</dt>
                <dd className="font-label text-foreground-900">{drama.episodes} 集</dd>
              </div>
              <div className="flex items-center gap-2">
                <dt className="text-foreground-500">年份</dt>
                <dd className="font-label text-foreground-900">{drama.year} 年</dd>
              </div>
              <div className="flex items-center gap-2">
                <dt className="text-foreground-500">适合人群</dt>
                <dd className="font-label text-foreground-900">{drama.audience}</dd>
              </div>
            </dl>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <WatchlistButton dramaId={drama.id} title={drama.title} />
              <Link
                to={genreSlug ? `/ranking?category=${genreSlug}` : "/ranking"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-100/70 px-5 py-3 text-sm font-label text-foreground-900 transition-colors hover:border-accent-500/70 hover:text-accent-400 cursor-pointer"
              >
                <i className="ri-links-line" aria-hidden="true"></i>
                找同款类似剧
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}